import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ref, getDownloadURL } from 'firebase/storage';
import { LuBookOpen, LuExternalLink, LuLoaderCircle, LuRefreshCw, LuTriangleAlert } from 'react-icons/lu';
import { storage } from '../../config/firebaseConfig';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import { useTranslation } from '../../i18n/LanguageContext';

const MANUAL_PATH = 'HowToUse/Habcare Klavuz.pdf';

/** "Kullanım Kılavuzu": the portal guide (a PDF in Firebase Storage). */
const UserManual = ({ isOpen, onClose }) => {
  const { t, language } = useTranslation();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fetchPdfUrl = useCallback(async () => {
    try {
      setLoading(true);
      setFailed(false);
      setPdfUrl(await getDownloadURL(ref(storage, MANUAL_PATH)));
    } catch (err) {
      console.error('Error fetching PDF:', err);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchPdfUrl();
  }, [isOpen, fetchPdfUrl]);

  const languageNote = t('manual.languageNote');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      lang={language}
      title={t('manual.title')}
      subtitle={languageNote || undefined}
      icon={(
        <GlassIcon gradient="tw-from-emerald-500 tw-to-teal-500 tw-shadow-emerald-500/30">
          <LuBookOpen aria-hidden="true" className="tw-h-6 tw-w-6" />
        </GlassIcon>
      )}
      actions={pdfUrl && !loading && !failed ? (
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="tw-inline-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-slate-900/[0.05] tw-px-3 tw-text-xs tw-font-semibold tw-text-slate-700 tw-transition-colors hover:tw-bg-slate-900/[0.09] dark:tw-bg-white/[0.07] dark:tw-text-slate-200 dark:hover:tw-bg-white/[0.12]"
        >
          <LuExternalLink aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
          {t('manual.openInNewTab')}
        </a>
      ) : null}
      closeLabel={t('common.close')}
      bodyClassName="tw-p-0"
    >
      <div className="tw-flex tw-h-[70vh] tw-flex-col tw-bg-slate-100 dark:tw-bg-black/20">
        {loading ? (
          <div className="tw-m-auto tw-flex tw-flex-col tw-items-center tw-gap-3 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
            <LuLoaderCircle aria-hidden="true" className="tw-h-8 tw-w-8 tw-animate-spin tw-text-teal-500" />
            {t('manual.loading')}
          </div>
        ) : failed ? (
          <div className="tw-m-auto tw-flex tw-max-w-sm tw-flex-col tw-items-center tw-gap-4 tw-px-6 tw-text-center">
            <LuTriangleAlert aria-hidden="true" className="tw-h-8 tw-w-8 tw-text-rose-500" />
            <p className="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{t('manual.error')}</p>
            <Button onClick={fetchPdfUrl}>
              <LuRefreshCw aria-hidden="true" className="tw-h-4 tw-w-4" />
              {t('common.retry')}
            </Button>
          </div>
        ) : (
          <iframe src={pdfUrl} title={t('manual.title')} className="tw-h-full tw-w-full tw-flex-1 tw-border-0" />
        )}
      </div>
    </Modal>
  );
};

UserManual.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UserManual;
